import { PlusCircleOutlined } from '@ant-design/icons';
import { Select, Space, Button, Tooltip, Input, Typography, message, Card, Checkbox } from 'antd';
import { memo, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useGetSkuRequestsHistory } from '../../hooks/useRequestsHistory';
import useLogout from '../../hooks/useLogout';
import './modalStyle.scss';
import { useGetSkuDetailsById } from '../../hooks/useSkus';
import { useGetCompanyListMinimal } from '../../hooks/useCompanies';
import { useGetOrderListMinimal } from '../../hooks/useOrders';
import { request, request_v2 } from '../../utils/axios-utils';
import { useGetAnglePresetById, useListAnglePresetsLite } from '../../hooks/useAnglePreset';
import { useListModelsForAssignment } from '../../hooks/useSkuModal';

const BOX_LEGACY_CLOSED = 900;
const BOX_AUDITED = 3000;
const BOX_OUTBOUNDED = 4000;
const { Meta } = Card;
const Request_Types = [
    'Reshoot',
    'RawQC Reset',
    'Mark As Support',
    'Product Damage',
    'Client Clarification',
    'Preset Change',
    'Resend Sku To Production',
    'rePII',
    "Hero to Fundus",
    'Change Order',
];

const BULK_HIDDEN_TYPES = new Set(['Product Damage', 'Reshoot']);

const RaiseRequests = ({
    selelctedRequestType,
    setSelectedRequestType,
    raisedRequestButtonData,
    closeDamageRequest,
    getReshootCategoriesOption,
    selectedCategoryForReshoot,
    setSelectedCategoryForReshoot,
    setWantNewCategoryToAdd,
    wantNewCategoryToAdd,
    newCategoryAdded,
    setNewCategoryAdded,
    addNewCategoryToList,
    requestComment,
    setRequestComment,
    raiseRequestConfirm,
    handleAngleSelection,
    selectedAngles,
    handleSelectAllAngles,
    isPresetChangeReshootRequired,
    setPresetChangeReshootRequired,
    selectedPresetId,
    setSelectedPresetId,
    selectedModel,
    setSelectedModel,
    isAutoApproveDD,
    setAutoApproveDD,
    autoApproveRequest,
    setAutoApproveRequest,
    targetCompanyCode,
    setTargetCompanyCode,
    targetOrderId,
    setTargetOrderId,
    bulkSkus = [],
    confirmLoading = false,
}) => {
    const isBulk = bulkSkus.length > 1;
    const [isAdmin, setIsAdmin] = useState(false);
    const requestTypes = isBulk ? Request_Types.filter(type => !BULK_HIDDEN_TYPES.has(type)) : Request_Types;
    const [skusHistory, setSkusHistory] = useState([]);
    const [checkedFlag, setCheckedFlag] = useState(false);
    const [isReshootSelectedForAngles, setReshootSelectedForAngles] = useState(false);
    const { logout } = useLogout();
    const onSuccess = data => {
        if (data?.data?.length > 0) {
            setSkusHistory(data?.data);
        } else {
            setSkusHistory([]);
        }
    };
    const onError = err => {
        console.log(err);
        message.error(err?.response?.data?.message);
        if (err?.response?.status == 401) {
            logout();
        }
    };
    useGetSkuRequestsHistory(isBulk ? null : raisedRequestButtonData?._id, onSuccess, onError);
    const { data: skuDetailsForAngles } = useGetSkuDetailsById(raisedRequestButtonData?._id, isReshootSelectedForAngles);
    const { data: companies, isFetching: isFetchingCompanies } = useGetCompanyListMinimal();
    const { data: targetOrders, isFetching: isFetchingTargetOrders } = useGetOrderListMinimal(targetCompanyCode);
    const presetChangeSkus = isBulk ? bulkSkus : [raisedRequestButtonData].filter(Boolean);
    const presetChangeCompanyCodes = [...new Set(presetChangeSkus.map(sku => sku?.article?.companyCode).filter(Boolean))];
    const presetChangeCompanyCode = presetChangeCompanyCodes.length === 1 ? presetChangeCompanyCodes[0] : null;
    const presetChangeCompanyId = companies?.find(company => company?.code === presetChangeCompanyCode)?._id;
    const { data: anglePresets = [], isFetching: isFetchingAnglePresets } = useListAnglePresetsLite(presetChangeCompanyCode);
    const { data: selectedPreset, isFetching: isFetchingSelectedPreset } = useGetAnglePresetById(null, selectedPresetId);
    const { data: models = [], isFetching: isFetchingModels } = useListModelsForAssignment(presetChangeCompanyId);
    const modelOccasions = models.flatMap(model =>
        (model?.occasions || []).map(occasion => ({
            ...occasion,
            name: model.name,
            image: occasion.image || model.baseFace,
        }))
    );
    const selectedModelPreviewUrl = selectedModel?.image?.thumbPath?.url || selectedModel?.image?.imagePath?.url;
    useEffect(() => {
        let cancelled = false;
        request_v2({ method: 'GET', url: '/requests/raiseOptions' })
            .then(({ data }) => {
                if (!cancelled) setIsAdmin(data?.isAdmin === true);
            })
            .catch(() => { });
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        setSelectedCategoryForReshoot('');
        setWantNewCategoryToAdd(false);
        setNewCategoryAdded('');
        setRequestComment('');
        setSelectedPresetId(null);
        setSelectedModel(null);
        setAutoApproveDD(false);
        setAutoApproveRequest(false);
        if (selelctedRequestType === 'Change Order') {
            setTargetCompanyCode(raisedRequestButtonData?.article?.companyCode || '');
            setTargetOrderId('');
        } else {
            setTargetCompanyCode('');
            setTargetOrderId('');
        }
    }, [
        selelctedRequestType,
        raisedRequestButtonData?.article?.companyCode,
        setAutoApproveDD,
        setAutoApproveRequest,
        setNewCategoryAdded,
        setRequestComment,
        setSelectedCategoryForReshoot,
        setSelectedModel,
        setSelectedPresetId,
        setTargetCompanyCode,
        setTargetOrderId,
        setWantNewCategoryToAdd,
    ]);

    function openCenteralisedWindow(url, title, w, h) {
        var parentCenter = {
            top: window.screenTop + window.innerHeight / 2,
            left: window.screenLeft + window.innerWidth / 2,
        };
        var offset = {
            top: parentCenter.top - h / 2,
            left: parentCenter.left - w / 2,
        };
        return window.open(url, title, 'resizeable=1,scrollbar=1' + 'width=' + w + ',height=' + h + ',top=' + offset.top + ',left=' + offset.left);
    }

    const packedBoxId = !isBulk ? raisedRequestButtonData?.outboundBoxId : null;
    const { data: packedBox } = useQuery(
        ['outboundBox', packedBoxId],
        async () => {
            const res = await request({ url: '/inventory/outbound/box', method: 'GET', params: { searchText: packedBoxId } });
            return res?.data?.data?.[0] || null;
        },
        { enabled: !!packedBoxId, refetchOnWindowFocus: false }
    );
    const boxIsAudited = packedBox?.status === BOX_AUDITED;
    const boxIsOutbounded =
        !!raisedRequestButtonData?.isOutbound || packedBox?.status === BOX_OUTBOUNDED || !!packedBox?.isOutbounded;
    const boxHasLeftAudit = boxIsAudited || boxIsOutbounded || packedBox?.status === BOX_LEGACY_CLOSED;
    const callbackNote = packedBoxId && (boxIsAudited || boxIsOutbounded) && (
        <div style={{ marginTop: 12, padding: 8, background: '#fff7e6' }}>
            Packed in box <b>{packedBoxId}</b>.
            {boxIsOutbounded
                ? ' Already outbounded — this becomes a client-side callback.'
                : ' This box is audited, so a callback request will be created.'}
        </div>
    );

    const requestedComponent = () => {
        if (selelctedRequestType === 'Product Damage') {
            openCenteralisedWindow(
                `https://master.d3uecwajju1iel.amplifyapp.com/?productIdentity=${raisedRequestButtonData?._id}&osbId=${raisedRequestButtonData?.osbid}`,
                'Product Damage Request',
                500,
                500
            );
            closeDamageRequest();
            return;
        }

        if (selelctedRequestType === 'Reshoot' || selelctedRequestType === 'RawQC Reset' || selelctedRequestType === 'Resend Sku To Production' || selelctedRequestType === 'Hero to Fundus') {
            return (
                <>
                    {selelctedRequestType === 'Reshoot' && callbackNote}
                    <div style={{ marginTop: 12 }}>
                        <span> Would You like to raise a {selelctedRequestType} request{isBulk ? ` for ${bulkSkus.length} SKUs` : ''}? </span>
                        <br />
                        <b>
                            <span style={{ color: 'red' }}>This step is final and cannot be undone.</span>
                        </b>
                    </div>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {selelctedRequestType === 'Reshoot' && (
                            <Space direction="horizontal" size={10} style={{ marginBottom: '5px' }}>
                                <div>If Yes, Please Choose Category*:</div>
                                <Select
                                    options={getReshootCategoriesOption()}
                                    value={selectedCategoryForReshoot}
                                    width={142}
                                    style={{ minWidth: '142px' }}
                                    onChange={e => {
                                        setSelectedCategoryForReshoot(e);
                                    }}
                                />
                                <span>
                                    {' '}
                                    <Tooltip title="Add more categories">
                                        <Button
                                            type="text"
                                            shape="circle"
                                            ghost
                                            icon={<PlusCircleOutlined />}
                                            onClick={() => setWantNewCategoryToAdd(true)}
                                        />
                                    </Tooltip>
                                </span>
                            </Space>
                        )}
                        {selelctedRequestType === 'RawQC Reset' && (
                            <Space direction="horizontal" size={10} style={{ marginBottom: '5px' }}>
                                <div>If Yes, Please Enter Category*:</div>
                                <Input
                                    value={selectedCategoryForReshoot}
                                    width={142}
                                    style={{ minWidth: '142px' }}
                                    onChange={e => {
                                        setSelectedCategoryForReshoot(e?.target?.value);
                                    }}
                                />
                            </Space>
                        )}
                        {selelctedRequestType === 'Reshoot' && wantNewCategoryToAdd && (
                            <Space direction="horizontal" size={10} style={{ marginBottom: '5px' }}>
                                <div> Add Category, If not found:</div>
                                <Input
                                    value={newCategoryAdded}
                                    width={120}
                                    style={{ minWidth: 120 }}
                                    onChange={e => {
                                        setNewCategoryAdded(e?.target?.value);
                                    }}
                                />
                                <span>
                                    {' '}
                                    <Button type="default" shape="square" onClick={addNewCategoryToList} disabled={!newCategoryAdded}>
                                        Add
                                    </Button>
                                </span>
                            </Space>
                        )}
                        <Space direction="horizontal" size={10}>
                            <span>Comment: </span>
                            <Input
                                onChange={e => setRequestComment(e.target.value)}
                                value={requestComment}
                                status={!requestComment || requestComment == '' ? 'error' : ''}
                                style={{ width: '100%', flexGrow: 1, width: '284px' }}
                            />
                        </Space>
                    </Space>
                </>
            );
        }

        if (
            selelctedRequestType === 'Mark As Support' ||
            selelctedRequestType === 'Client Clarification' ||
            selelctedRequestType === 'Preset Change' ||
            selelctedRequestType === 'rePII' ||
            selelctedRequestType === 'Change Order'
        ) {
            return (
                <>
                    {selelctedRequestType === 'Preset Change' && isPresetChangeReshootRequired && callbackNote}
                    {selelctedRequestType === 'rePII' && packedBoxId && boxHasLeftAudit && (
                        <div style={{ marginTop: 12, padding: 8, background: '#fff7e6' }}>
                            Packed in box <b>{packedBoxId}</b>. A callback will be raised and this article will not be
                            blocked in outbound. After it comes back and PII is done, it will be marked as support.
                        </div>
                    )}
                    <div style={{ marginTop: 12 }}>
                        <span> Would You like to raise a {selelctedRequestType} request{isBulk ? ` for ${bulkSkus.length} SKUs` : ''}? </span>
                        <br />
                        {selelctedRequestType === 'Client Clarification' && (
                            <>
                                <br />
                                <span>
                                    <strong>Note: On approval, the SLA will be paused until the DD is approved for selected product.</strong>
                                </span>
                                <br />
                            </>
                        )}
                        {selelctedRequestType === 'Change Order' && !isBulk && (
                            <>
                                <br />
                                <span>
                                    Current order: {raisedRequestButtonData?.article?.companyCode || '—'} /{' '}
                                    {raisedRequestButtonData?.article?.orderName || '—'}
                                </span>
                                <br />
                            </>
                        )}
                        <b>
                            <span style={{ color: 'red' }}>This step is final and cannot be undone.</span>
                        </b>
                    </div>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Space direction="horizontal" size={10}>
                            <span>Comment: </span>
                            <Input
                                onChange={e => setRequestComment(e.target.value)}
                                value={requestComment}
                                status={!requestComment || requestComment == '' ? 'error' : ''}
                                style={{ width: '100%', flexGrow: 1, width: '284px' }}
                            />
                        </Space>
                        {selelctedRequestType === 'Preset Change' && (
                            <>
                                {presetChangeCompanyCodes.length !== 1 && (
                                    <Typography.Text type="danger">
                                        Preset Change can only be raised for SKUs from the same client.
                                    </Typography.Text>
                                )}
                                <Space direction="horizontal" size={10}>
                                    <span>{isAutoApproveDD ? 'New Preset*: ' : 'New Preset (optional): '}</span>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select preset"
                                        loading={isFetchingAnglePresets}
                                        disabled={!presetChangeCompanyCode}
                                        optionFilterProp="label"
                                        style={{ minWidth: 280 }}
                                        value={selectedPresetId || undefined}
                                        onChange={value => setSelectedPresetId(value || null)}
                                        options={anglePresets.map(preset => ({
                                            label: `${preset.name}${preset.alias ? ` (${preset.alias})` : ''}`,
                                            value: preset._id,
                                        }))}
                                    />
                                </Space>
                                {selectedPresetId && (
                                    <Space direction="vertical" size={8}>
                                        <Typography.Text strong>Preset angles</Typography.Text>
                                        {isFetchingSelectedPreset ? (
                                            <Typography.Text>Loading preview...</Typography.Text>
                                        ) : (
                                            <Space size={[8, 8]} wrap>
                                                {(selectedPreset?.angles || []).map(angle => (
                                                    <Card
                                                        key={angle._id}
                                                        size="small"
                                                        style={{ width: 140 }}
                                                        cover={
                                                            angle?.angle?.file?.thumbPath?.url ? (
                                                                <img
                                                                    alt={angle?.angle?.name}
                                                                    src={angle.angle.file.thumbPath.url}
                                                                    style={{ height: 120, objectFit: 'contain' }}
                                                                />
                                                            ) : null
                                                        }
                                                    >
                                                        <Meta title={angle?.angle?.name} description={angle?.angle?.shootType} />
                                                    </Card>
                                                ))}
                                            </Space>
                                        )}
                                    </Space>
                                )}
                                <Space direction="horizontal" size={10}>
                                    <span>{isAutoApproveDD ? 'Model*: ' : 'Model (optional): '}</span>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select model and occasion"
                                        loading={isFetchingModels || isFetchingCompanies}
                                        disabled={!presetChangeCompanyId}
                                        optionFilterProp="label"
                                        style={{ minWidth: 280 }}
                                        value={selectedModel ? `${selectedModel.modelId}|${selectedModel.occassion}` : undefined}
                                        onChange={value => {
                                            const selected = modelOccasions.find(
                                                item => `${item.modelId}|${item.occassion}` === value
                                            );
                                            setSelectedModel(selected || null);
                                        }}
                                        options={modelOccasions.map(item => ({
                                            label: `${item.name} (${item.occassion})`,
                                            value: `${item.modelId}|${item.occassion}`,
                                        }))}
                                    />
                                </Space>
                                {selectedModelPreviewUrl && (
                                    <Card
                                        size="small"
                                        style={{ width: 180 }}
                                        cover={
                                            <img
                                                alt={`${selectedModel.name} ${selectedModel.occassion}`}
                                                src={selectedModelPreviewUrl}
                                                style={{ height: 160, objectFit: 'contain' }}
                                            />
                                        }
                                    >
                                        <Meta title={selectedModel.name} description={selectedModel.occassion} />
                                    </Card>
                                )}
                                <Space direction="horizontal" size={10}>
                                    <span>Reshoot Required: </span>
                                    <Checkbox
                                        checked={isPresetChangeReshootRequired}
                                        onChange={e => setPresetChangeReshootRequired(e.target.checked)}
                                    />
                                </Space>
                                <Space direction="vertical" size={0}>
                                    <Checkbox checked={isAutoApproveDD} onChange={e => setAutoApproveDD(e.target.checked)}>
                                        Auto-approve DD after preset change
                                    </Checkbox>
                                    <Typography.Text type="secondary">
                                        Requires a preset and model. SKU goes to RawQC, or Shoot when reshoot is required.
                                    </Typography.Text>
                                </Space>
                            </>
                        )}
                        {selelctedRequestType === 'Change Order' && (
                            <>
                                <Space direction="horizontal" size={10}>
                                    <span>Company: </span>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select company"
                                        loading={isFetchingCompanies}
                                        style={{ minWidth: 220 }}
                                        optionFilterProp="label"
                                        value={targetCompanyCode || undefined}
                                        onChange={value => {
                                            setTargetCompanyCode(value || '');
                                            setTargetOrderId('');
                                        }}
                                        options={(companies || []).map(company => ({
                                            label: `${company?.code} — ${company?.name}`,
                                            value: company?.code,
                                        }))}
                                    />
                                </Space>
                                <Space direction="horizontal" size={10}>
                                    <span>Order: </span>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select order"
                                        loading={isFetchingTargetOrders}
                                        disabled={!targetCompanyCode}
                                        style={{ minWidth: 280 }}
                                        optionFilterProp="label"
                                        value={targetOrderId || undefined}
                                        onChange={value => setTargetOrderId(value || '')}
                                        options={(targetOrders || []).map(order => ({
                                            label: order?.name,
                                            value: order?._id,
                                        }))}
                                    />
                                </Space>
                            </>
                        )}
                    </Space>
                </>
            );
        }
    };

    const requestedHistoryComponent = () => {
        let historyItems = [...skusHistory].reverse();
        return (
            <Space className="" style={{ width: '100%', marginTop: '2rem', padding: 0 }} direction="vertical" size={3}>
                <hr />
                <Typography.Title level={5} style={{ fontSize: 14 }}>
                    Requests Raised
                </Typography.Title>
                <Space
                    style={{ width: '100%', marginTop: '1rem', padding: 0, position: 'relative', maxHeight: '400px', overflow: 'auto' }}
                    direction="vertical"
                    size={8}
                >
                    {historyItems?.map(item => {
                        return (
                            <Card
                                size="small"
                                title={[<div style={{ fontWeight: 'bold' }}>{item?.requestType} </div>]}
                                extra={[
                                    <div style={{ fontSize: 13 }}>
                                        <strong>Raised By:</strong> {item?.raisedBy}{' '}
                                    </div>,
                                ]}
                                style={
                                    {
                                        // width: 300,
                                    }
                                }
                            >
                                <div style={{ fontSize: 13 }}>
                                    <div>
                                        <strong>Comment:</strong> {item?.comment}{' '}
                                    </div>
                                    {item?.requestType === 'PresetChange' && (
                                        <>
                                            <div>
                                                <strong>Preset:</strong>{' '}
                                                {item?.newAnglePreset
                                                    ? `${item.newAnglePreset.name}${item.newAnglePreset.alias ? ` (${item.newAnglePreset.alias})` : ''}`
                                                    : 'Not specified'}
                                            </div>
                                            <div>
                                                <strong>Model:</strong>{' '}
                                                {item?.newModel ? `${item.newModel.name} (${item.newModel.occassion})` : 'Not specified'}
                                            </div>
                                            <div>
                                                <strong>Reshoot Required:</strong> {item?.reshootRequired ? 'Yes' : 'No'}
                                            </div>
                                            <div>
                                                <strong>Auto-approve DD:</strong> {item?.autoApproveDD ? 'Yes' : 'No'}
                                            </div>
                                        </>
                                    )}
                                    <div>
                                        <strong>Raised At:</strong> {new Date(item?.creationTimestamp).toLocaleString()}{' '}
                                    </div>
                                    <div>
                                        <strong>Status:</strong> {item?.stage}{' '}
                                    </div>
                                    <div>
                                        <strong>Action:</strong> {item?.actionType ?? ''} by {item?.actionBy ?? ''}{' '}
                                    </div>
                                    {item?.actionType ? (
                                        <div>
                                            <strong>{item?.actionType} at:</strong> {new Date(item?.actionTimestamp).toLocaleString()}{' '}
                                        </div>
                                    ) : null}
                                </div>
                            </Card>
                        );
                    })}
                </Space>
            </Space>
        );
    };
    useEffect(() => {
        if (selectedAngles?.length < skuDetailsForAngles?.angles?.length) {
            setCheckedFlag(false);
        }
    }, [selectedAngles]);

    return (
        <div className="">
            {isBulk ? (
                <div style={{ marginBottom: 12, fontSize: 12, color: '#595959' }}>
                    {bulkSkus
                        .slice(0, 12)
                        .map(sku => sku?.csvData?.EAN || sku?.csvData?.barcode || sku?.skuName)
                        .filter(Boolean)
                        .join(', ')}
                    {bulkSkus.length > 12 ? ` +${bulkSkus.length - 12} more` : ''}
                </div>
            ) : null}
            <Space direction="horizontal" size={12} style={{ width: '100%' }}>
                <label htmlFor="shoot_selection">Select Request Type:</label>
                <Select
                    value={selelctedRequestType}
                    id="shoot_selection"
                    onChange={value => {
                        setSelectedRequestType(value);
                        if (value === 'Reshoot') {
                            setReshootSelectedForAngles(true);
                        } else {
                            setReshootSelectedForAngles(false);
                        }
                    }}
                    style={{ minWidth: 260, maxWidth: 260, width: '80%' }}
                >
                    {requestTypes.map((request, index) => {
                        return <Select.Option key={index} value={request}>{`Raise ${request} Request`}</Select.Option>;
                    })}
                </Select>
            </Space>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {requestedComponent()}
            </Space>
            {selelctedRequestType === 'Reshoot' && skuDetailsForAngles?.angles?.length > 0 && (
                <Space direction="vertical" className="mt-1 w-100">
                    <div className="angles-header">
                        <label>Angles</label>
                        <Checkbox
                            checked={checkedFlag}
                            onChange={e => {
                                handleSelectAllAngles(e.target.checked);
                                setCheckedFlag(e.target.checked);
                            }}
                        >
                            Select All
                        </Checkbox>
                    </div>
                    <Space size={[8, 16]} wrap className="anglesWrapper">
                        {skuDetailsForAngles?.angles?.map(el => {
                            return (
                                <Card
                                    hoverable
                                    style={{
                                        width: 180,
                                    }}
                                    className={`angles-detail-card ${selectedAngles.includes(el?._id) ? 'activeCard' : ''}`}
                                    onClick={() => handleAngleSelection(el?._id)}
                                    cover={
                                        <div className="card-cover-box">
                                            <img
                                                className="mainCoverImg"
                                                alt={el?.angle?.file?.imagePath?.url}
                                                src={el?.angle?.file?.thumbPath?.url}
                                            />
                                            {el?.file?.image?.thumbPath?.url && (
                                                <img
                                                    className="subFile_img"
                                                    alt={el?.file?.image?.imagePath?.url}
                                                    src={el?.file?.image?.thumbPath?.url}
                                                />
                                            )}
                                        </div>
                                    }
                                >
                                    <Meta title={el?.angle?.name} description={el?.angle?.shootType} />
                                </Card>
                            );
                        })}
                    </Space>
                </Space>
            )}

            {isAdmin && !!selelctedRequestType && selelctedRequestType !== 'Product Damage' && (
                <Checkbox checked={autoApproveRequest} onChange={e => setAutoApproveRequest(e.target.checked)} style={{ marginTop: '1rem' }}>
                    Auto-approve and skip email
                </Checkbox>
            )}
            <Space direction="horizontal" size={12} style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
                {!!selelctedRequestType && selelctedRequestType !== 'Product Damage' && (
                    <Button type="default" onClick={closeDamageRequest} size="small">
                        Cancel
                    </Button>
                )}
                {!!selelctedRequestType && selelctedRequestType !== 'Product Damage' && (
                    <Button
                        type="primary"
                        onClick={() => raiseRequestConfirm()}
                        size="small"
                        loading={confirmLoading}
                        disabled={
                            confirmLoading ||
                            !requestComment ||
                            requestComment == '' ||
                            (selelctedRequestType !== 'Mark As Support' &&
                                selelctedRequestType !== 'Resend Sku To Production' &&
                                selelctedRequestType !== 'Client Clarification' &&
                                selelctedRequestType !== 'Preset Change' &&
                                selelctedRequestType !== 'rePII' &&
                                selelctedRequestType !== 'Hero to Fundus' &&
                                selelctedRequestType !== 'Change Order' &&
                                !selectedCategoryForReshoot) ||
                            (selelctedRequestType === 'Reshoot' && selectedAngles.length == 0) ||
                            (selelctedRequestType === 'Preset Change' &&
                                (presetChangeCompanyCodes.length !== 1 ||
                                    (isAutoApproveDD && (!selectedPresetId || !selectedModel)))) ||
                            (selelctedRequestType === 'Change Order' &&
                                (!targetOrderId ||
                                    (!isBulk && String(targetOrderId) === String(raisedRequestButtonData?.oid))))
                        }
                    >
                        {isBulk ? `I Confirm (${bulkSkus.length})` : 'I Confirm'}
                    </Button>
                )}
            </Space>
            {!isBulk && skusHistory?.length > 0 ? (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {requestedHistoryComponent()}
                </Space>
            ) : null}
        </div>
    );
};

export default memo(RaiseRequests);
